<script setup lang="ts">
import { defineAsyncComponent, ref, watch } from 'vue';
import NavLink from '../nav-link.vue';
import model from '@/ts/model';
import { sendUserLogoutRequest } from '@/ts/services/casino/casino-login-service';
import { LogoutTypes } from '@/ts/types/casino-service-response-types';
import { getLangText } from '@/ts/utils/model-utils';
import vClickOutside from '@/ts/directives/v-click-outside';
import UserIcon from '@/assets/icons/user-icon.svg?raw';
import vBpType from '@/ts/directives/v-bp-type';
import LoadingSpinner from '../loading-spinner.vue';

const MainNavUserFunClubAvatar = defineAsyncComponent({
    loader: () => import('./main-nav-user-fun-club-avatar.vue'),
    loadingComponent: LoadingSpinner,
});

const userMenuItemsDisplayed = ref<boolean>(false);

watch(() => model.userLoggedIn, value => {
    if(value) return;
    userMenuItemsDisplayed.value = false;
});

watch(() => model.activeModal, () => {
    userMenuItemsDisplayed.value = false;
});

model.on('Navigation', () => {
    userMenuItemsDisplayed.value = false;
});

</script>

<template>
    <div class="user-menu-items-wrapper" v-click-outside="() => userMenuItemsDisplayed = false">
        <div 
            @click.prevent="userMenuItemsDisplayed = !userMenuItemsDisplayed" 
            v-mobile 
            class="avatar"
            v-bp-type
        >
            <template v-if="model.bonusPointsType == 'fun-club'">
                <main-nav-user-fun-club-avatar :small-logo="true" :width-auto="true" />
            </template>
            <template v-else>
                <div class="bg" v-bp-type></div>
                <div class="icon" v-html="UserIcon" v-bp-type></div>
            </template>
        </div>
        <div class="user-menu-items" :class="{displayed: userMenuItemsDisplayed}">
            <nav-link :id="'cashier'" :label="'CashierLabel'" class="menu-item" location="user-menu-items" />
            <nav-link :id="'account'" :label="'AccountLabel'" class="menu-item" location="user-menu-items" />
            <nav-link :id="'change-password'" :label="'SecurityLabel'" class="menu-item" location="user-menu-items" />
            <nav-link :id="'responsible-gaming-form'" :label="'ResponsibleGamingLabel'" class="menu-item" location="user-menu-items" />
            <template v-if="model.bonusPointsType == 'fun-club'">
                <nav-link :id="'user-fun-club'" :label="'FunClubLabel'" class="menu-item" location="user-menu-items" />
            </template>
            <nav-link :id="'account-verification'" :label="'VerificationLabel'" class="menu-item" location="user-menu-items" />
            <nav-link :id="'game-history'" :label="'GameHistoryLabel'" class="menu-item" location="user-menu-items" />
            <a href="#" @click.prevent.stop="sendUserLogoutRequest(LogoutTypes.UserLogout)">{{ getLangText("LogoutLabel") }}</a>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    @use 'sass:color';
    .user-menu-items-wrapper{
        position: relative;
        & > .avatar{
            display: flex;
            position: relative;
            background-position: center;
            background-repeat: no-repeat;
            background-size: contain;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: transform .25s;
            margin-right: 5px;
            &.fun-club{
                width: 60px;
                height: 60px;
                &[data-mobile]{
                    width: 40px;
                    height: 40px;
                }
                &:not([data-mobile]){
                    &:hover{
                        transform: scale(1.1);
                    }
                }
            }
            &.stellar-stream{
                &:not([data-mobile]){
                    &:hover{
                        @include shared.site() using($site-key){
                            $color: shared.theme-color('comp3_3', $site-key);
                            & > :deep(.bg){
                                box-shadow: inset 0 0 1px 1.75px $color;
                            }
                            & > :deep(.icon){
                                color: $color;
                            }
                        }
                    }
                }
            }
            & > :deep(.bg){
                position: relative;
                background-position: center;
                background-repeat: no-repeat;
                background-size: contain;
                &.fun-club{
                    width: 52px;
                    height: 52px;
                }
                &.stellar-stream{
                    border: 0 solid #fff;
                    border-radius: 100%;
                    width: 30px;
                    height: 30px;
                    box-shadow: inset 0 0 1px 1.75px #fff;
                    transition: box-shadow .25s;
                }
            }
            & > :deep(.icon){
                position: absolute;
                width: 70%;
                height: 70%;
                background-position: center;
                background-repeat: no-repeat;
                background-size: contain;
                top:50%;
                left:50%;
                transform: translateX(-50%) translateY(-50%);
                transition: color .25s;
                &.stellar-stream{
                    width: 55%;
                    height: 55%;
                    &,
                    & > *{
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        filter: drop-shadow(0 0 0.25px #ffffff6e);
                    }
                }
            }
        }
        & > .user-menu-items{
            position: absolute;
            display: flex;
            flex-direction: column;
            top: 120%;
            right: 20px;
            border-radius: 15px;
            padding: 5px 10px;
            transition: opacity .25s;
            white-space: nowrap;
            &.displayed{
                opacity: 1;
                pointer-events: all;
            }
            &:not(.displayed){
                opacity: 0;
                pointer-events: none;
            }
            @include shared.site() using($site-key){
                background: linear-gradient(0deg, shared.theme-color('primary_3', $site-key) 0%, #000000 100%);
            }
            & > a{
                &:link,
                &:hover,
                &:active,
                &:visited{
                    text-decoration: none;
                    margin-top: 7px;
                    padding-top: 3px;
                    padding-bottom: 3px;
                    transition: color .25s;
                    color: #fff;
                    @include shared.casino-section-title();
                    &:not(:last-child){
                        @include shared.site() using($site-key){
                            border-bottom: 1px dashed shared.theme-color("comp3_3", $site-key);
                        }
                    }
                }
                &:hover{
                    @include shared.site() using($site-key){
                        color: shared.theme-color("comp3_3", $site-key);
                    }
                }
            }
        }
    }
</style>